/**
 * Browser Detection
 * Browser detection functions attached to the OCMS object.
 * @namespace
 */

import {
  isEmpty,
} from '../Utils';

const userAgent = navigator.userAgent;

/** The platform string. */
const platformString = navigator.platform;

let mbLayoutEngineIsWebKit = null;

let mbOSIsAndroid = null;

let mbOSIsIOS = null;

let mbDeviceIsIPAD = null;

let mbBrowserIsIE = null;

let mbBrowserIsEdge = null;

let mbBrowserIsIEMobile = null;

let mbBrowserIsFirefox = null;

let mbBrowserIsChrome = null;

let mbBrowserIsOpera = null;

let mbBrowserVersion = -1;

let mbTouchEventsSupported = null;

let mbPositionFixedSupported = null;

/**
 * Determines if the layout engine is WebKit.
 *
 * @return {Boolean} : Whether or not the layout engine is WebKit.
*/
function isWebKit() {
  if (mbLayoutEngineIsWebKit === null) {
    mbLayoutEngineIsWebKit = (navigator.appVersion.match(/WebKit/) != null);
  }

  return mbLayoutEngineIsWebKit;
}

/**
 * Determines if the OS is Android.
 *
 * @return {Boolean} : Whether or not the OS is Android.
*/
function isAndroid() {
  if (mbOSIsAndroid === null) {
    mbOSIsAndroid = (navigator.appVersion.match(/Android/) != null);
  }

  return mbOSIsAndroid;
}

/**
 * Determines if the OS is iOS.
 *
 * @return {Boolean} : Whether or not the OS is iOS.
*/
function isIOS() {
  if (mbOSIsIOS === null) {
    if (!isEmpty(platformString)) {
      mbOSIsIOS = ((/iphone/i.test(platformString)) || (/ipod/i.test(platformString)) || (/ipad/i.test(platformString)));
    } else {
      mbOSIsIOS = false;
    }
  }

  return mbOSIsIOS;
}

/**
 * Determines if the device is the iPad.
 *
 * @return {Boolean} : Whether or not the device is the iPad.
*/
function isIPAD() {
  if (mbDeviceIsIPAD === null) {
    if (!isEmpty(platformString)) {
      mbDeviceIsIPAD = (/ipad/i.test(platformString));
    } else {
      mbDeviceIsIPAD = false;
    }
  }

  return mbDeviceIsIPAD;
}

/**
 * Determines if the browser is IE.
 *
 * @return {Boolean} : Whether or not the browser is IE.
*/
function isIE() {
  if (mbBrowserIsIE === null) {
    mbBrowserIsIE = (userAgent.match(/MSIE/) != null) || (userAgent.match(/Trident/) != null);
  }

  return mbBrowserIsIE;
}

/**
 * Determines if the browser is Edge.
 *
 * @return {Boolean} : Whether or not the browser is Edge.
*/
function isEdge() {
  if (mbBrowserIsEdge === null) {
    mbBrowserIsEdge = (userAgent.match(/Edge\//) != null);
  }

  return mbBrowserIsEdge;
}

/**
 * Determines if the browser is IE Mobile.
 *
 * @return {Boolean} : Whether or not the browser is IE Mobile.
*/
function isIEMobile() {
  if (mbBrowserIsIEMobile === null) {
    mbBrowserIsIEMobile = (navigator.appVersion.match(/IEMobile/) != null);
  }

  return mbBrowserIsIEMobile;
}

/**
 * Determines if the browser is Firefox.
 *
 * @return {Boolean} : Whether or not the browser is Firefox.
*/
function isFirefox() {
  if (mbBrowserIsFirefox === null) {
    mbBrowserIsFirefox = (userAgent.match(/firefox/i) != null);
  }

  return mbBrowserIsFirefox;
}

/**
 * Determines if the browser is Chrome.
 *
 * @return {Boolean} : Whether or not the browser is Chrome.
*/
function isChrome() {
  if (mbBrowserIsChrome === null) {
    mbBrowserIsChrome = (navigator.appVersion.match(/Chrome/) != null);
  }

  return mbBrowserIsChrome;
}

/**
 * Determines if the browser is Opera.
 *
 * @return {Boolean} : Whether or not the browser is Opera.
*/
function isOpera() {
  if (mbBrowserIsOpera === null) {
    mbBrowserIsOpera = (window.opera != null);
  }

  return mbBrowserIsOpera;
}

function getBrowserVersion() {
  if (isIE()) {
    // IE
    const regExp = new RegExp('(?:MSIE |Trident.*rv[ :])([0-9]+[.0-9]*)');
    if (regExp.exec(userAgent) != null) {
      mbBrowserVersion = parseFloat(RegExp.$1);
    }
  } else if (isEdge()) {
    // Edge
    const regExp = new RegExp('Edge/([0-9]+[.0-9]*)');
    if (regExp.exec(userAgent) != null) {
      mbBrowserVersion = parseFloat(RegExp.$1);
    }
  } else if (isIOS()) {
    // iOS
    const regExp = new RegExp('OS (\\d)', 'i');
    if (regExp.exec(userAgent) != null) {
      mbBrowserVersion = parseFloat(RegExp.$1);
    }
  } else if (isFirefox()) {
    // Firefox
    const regExp = new RegExp('firefox/([0-9]+[.0-9]*)', 'i');
    if (regExp.exec(userAgent) != null) {
      mbBrowserVersion = parseFloat(RegExp.$1);
    }
  } else if (isChrome()) {
    // Chrome
    const regExp = new RegExp('Chrome/([0-9]+[.0-9]*)');
    if (regExp.exec(userAgent) != null) {
      mbBrowserVersion = parseFloat(RegExp.$1);
    }
  } else if (isOpera()) {
    // Opera
    mbBrowserVersion = (window.opera.version) ? parseFloat(window.opera.version()) : 0;
  }

  return mbBrowserVersion;
}

function isEventSupported(undef) {
  const tagnames = {
    select: 'input',
    change: 'input',
    submit: 'form',
    reset: 'form',
    error: 'img',
    load: 'img',
    abort: 'img',
  };

  function isEventSupportedBis(eventName, element) {
    let elementB = element || document.createElement(tagnames[eventName] || 'div');
    const eventNameFull = `on${eventName}`;

    let isSupported = (eventNameFull in elementB);

    if (!isSupported) {
      // if it has no `setAttribute` (i.e. doesn't implement Node interface), try generic element
      if (!elementB.setAttribute) {
        elementB = document.createElement('div');
      }
      if (elementB.setAttribute && elementB.removeAttribute) {
        elementB.setAttribute(eventNameFull, '');
        isSupported = typeof elementB[eventNameFull] === 'function';

        // if property was created, "remove it" (by setting value to `undefined`)
        if (typeof elementB.eventNameFull !== 'undefined') {
          elementB.eventNameFull = undef;
        }
        elementB.removeAttribute(eventNameFull);
      }
    }

    elementB = null;
    return isSupported;
  }

  return isEventSupportedBis;
}

/**
 * Determines if touch events are supported.
 * @type {Boolean}
 * @private
*/
function areTouchEventsSupported() {
  if (mbTouchEventsSupported == null) {
    mbTouchEventsSupported = (
      isEventSupported('touchmove') ||
      isEventSupported('MSPointerMove')
    );
  }

  return mbTouchEventsSupported;
}

/**
 * Determines if the device supports fixed positioning.
 *
 * @return {Boolean} : Whether or not the device supports fixed positioning.
*/
function isPositionFixedSupported() {
  if (mbPositionFixedSupported === null) {
    if (isIOS()) {
      if (getBrowserVersion() >= 5) {
        mbPositionFixedSupported = true;
      } else {
        mbPositionFixedSupported = false;
      }
    } else if (isIEMobile()) {
      if (getBrowserVersion() > 9) {
        mbPositionFixedSupported = true;
      } else {
        mbPositionFixedSupported = false;
      }
    } else {
      const testDiv = document.createElement('div');
      const controlDiv = testDiv.cloneNode(false);
      let fake = false;
      const root = document.body || (() => {
        fake = true;
        return document.documentElement.appendChild(document.createElement('body'));
      });

      const oldCssText = root.style.cssText;
      root.style.cssText = 'padding: 0; margin: 0;';
      testDiv.style.cssText = 'position: fixed; top: 42px;';
      root.appendChild(testDiv);
      root.appendChild(controlDiv);

      mbPositionFixedSupported = (testDiv.offsetTop !== controlDiv.offsetTop);

      root.removeChild(testDiv);
      root.removeChild(controlDiv);
      root.style.cssText = oldCssText;

      if (fake) {
        document.documentElement.removeChild(root);
      }
    }
  }

  return mbPositionFixedSupported;
}
export {
  isWebKit,
  isAndroid,
  isIOS,
  isIPAD,
  isIE,
  isEdge,
  isIEMobile,
  isFirefox,
  isChrome,
  isOpera,
  getBrowserVersion,
  areTouchEventsSupported,
  isPositionFixedSupported,
  isEventSupported,
};



// WEBPACK FOOTER //
// ./js/common/tools/BrowserDetection.js